import {useEffect, useRef} from 'react';

import {useIntersectionObserver} from 'usehooks-ts';

import {selectThreadById} from 'features/threads/selectors/select_thread_by_id';
import {getPost} from 'mattermost-redux/selectors/entities/posts';
import {useAppDispatch, useAppSelector} from 'stores/redux_store';
import {fetchUserThread} from 'features/threads/actions/fetch_user_thread';
import {getCurrentTeamId} from 'mattermost-redux/selectors/entities/teams';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/common';
import {getThreadPostFollowState} from 'features/threads/selectors/get_thread_post_follow_state';
import {isThreadOpened} from 'features/threads/selectors/is_thread_opened';

export function useFetchThread<RefElement extends Element>(threadId: string) {
    const threadElementRef = useRef<RefElement>(null);
    const dispatch = useAppDispatch();
    const isRootPostWithNoThread = useAppSelector((state) => {
        const thread = selectThreadById(state, threadId);
        const post = getPost(state, threadId);
        const hasReplies = post?.reply_count ? post?.reply_count > 0 : false;

        const hasThread = thread?.id;

        return hasReplies && !hasThread;
    });

    const isFollowing = useAppSelector((state) => getThreadPostFollowState(state, threadId));
    const isOpenedThread = useAppSelector((state) => isThreadOpened(state, threadId));
    const currentTeamId = useAppSelector(getCurrentTeamId);
    const currentUserId = useAppSelector(getCurrentUserId);

    const entry = useIntersectionObserver(threadElementRef, {
        freezeOnceVisible: true,
    });

    const isVisible = entry?.isIntersecting;

    const shouldFetch = isFollowing && isVisible && isRootPostWithNoThread && !isOpenedThread;

    useEffect(() => {
        if (shouldFetch) {
            dispatch(
                fetchUserThread({
                    teamId: currentTeamId,
                    threadId,
                    userId: currentUserId,
                    extended: true,
                }),
            );
        }
    }, [shouldFetch, threadId, currentTeamId, currentUserId, dispatch]);

    return {
        threadElementRef,
    };
}
